import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuthStore } from '../../store/authStore';
import Button from '../../components/Button';
import AvatarUpload from '../../components/AvatarUpload';
import OnboardingProgress from '../../components/OnboardingProgress';

const schema = z.object({
  full_name: z.string().min(2, 'Full name is required'),
  display_name: z.string().min(2, 'Display name is required'),
  bio: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

export default function OnboardingName() {
  const navigate = useNavigate();
  const { user, updateProfile, isOAuth } = useAuthStore();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [avatarUrl, setAvatarUrl] = React.useState<string | null>(user?.avatar_url || null);

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      full_name: user?.full_name || '',
      display_name: user?.display_name || '',
      bio: user?.bio || '',
    },
  });

  const onSubmit = async (data: FormData) => {
    try {
      console.log('Starting form submission:', data);
      setIsSubmitting(true);
      setError(null);

      if (!user?.id) {
        throw new Error('No user found. Please try signing in again.');
      }

      const updateData: any = {
        full_name: data.full_name,
        display_name: data.display_name,
        bio: data.bio,
      };

      // Handle avatar updates
      // Always include avatar_url in update to handle both uploads and deletions
      // null will work with Avatar component's fallback system
      updateData.avatar_url = avatarUrl;

      console.log('Updating profile...', updateData);
      await updateProfile(updateData);

      console.log('Profile updated successfully');
      navigate('/onboarding/profession');
    } catch (error: any) {
      console.error('Form submission error:', error);
      setError(error.message || 'Failed to update profile. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAvatarChange = (url: string) => {
    // Handle both new uploads and deletions
    // Empty string from AvatarUpload means deleted, convert to null for Avatar fallback
    // null means no avatar, actual URL means new/updated
    setAvatarUrl(url === '' ? null : url);
  };

  return (
    <div className="min-h-screen bg-surface-light px-4 py-8">
      <OnboardingProgress currentStep="name" />
      <div className="max-w-md mx-auto">
        <h1 className="text-3xl font-bold font-dillan text-primary mb-2 text-center">
          Let's get to know you!
        </h1>
        <p className="text-neutral mb-6 text-l text-center">
          Please tell us about yourself.
        </p>

        {error && (
          <div className="p-4 rounded-full bg-secondary-dark bg-opacity-10 text-primary">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Profile Picture Upload - Always show, with current avatar if exists */}
          <div>
            <AvatarUpload
              onUploadComplete={handleAvatarChange}
              className="mb-8"
              initialImage={user?.avatar_url}
            />
          </div>

          <div>
            <input
              type="text"
              id="full_name"
              {...register('full_name')}
              className="mb-4 w-64 h-12 px-4 rounded-full border-4 border-neutral bg-surface-light text-neutral placeholder:text-neutral focus:border-neutral focus:ring-0 
      focus:outline-none font-dillan text-m mx-auto block"
              placeholder="Full Name"
              disabled={isSubmitting}
            />
            {errors.full_name && (
              <p className="mt-1 text-sm text-primary pl-4">{errors.full_name.message}</p>
            )}
          </div>

          <div>
            <input
              type="text"
              id="display_name"
              {...register('display_name')}
              className="mb-4 w-64 h-12 px-4 rounded-full border-4 border-neutral bg-surface-light text-neutral placeholder:text-neutral focus:border-neutral focus:ring-0 focus:outline-none font-dillan text-m mx-auto block"
              placeholder="Display Name"
              disabled={isSubmitting}
            />
            {errors.display_name && (
              <p className="mt-1 text-sm text-primary pl-4">{errors.display_name.message}</p>
            )}
          </div>

          <div>
            <input
              type="text"
              id="bio"
              {...register('bio')}
              className="w-64 h-12 px-4 rounded-full border-4 border-neutral bg-surface-light text-neutral placeholder:text-neutral focus:border-neutral focus:ring-0 focus:outline-none font-dillan text-m  mx-auto block"
              placeholder="Bio"
              disabled={isSubmitting}
            />
          </div>

          <Button 
            type="submit" 
            variant="primary" 
            className="mt-8 w-64 mx-auto block text-surface-light"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Continue'}
          </Button>
        </form>
      </div>
    </div>
  );
}